import { type ReactNode, useState } from 'react';
import { Badge, Button, Checkbox, Divider, Group, Loader, Modal, Popover, Stack, Switch, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconChevronUp,
    IconCloud,
    IconDeviceDesktopDown,
    IconPlayerRecord,
    IconPlayerStop,
} from '@tabler/icons-react';
import { useRoomContext } from '@livekit/components-react';
import type { Participant } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import { formatDuration } from '../call-duration';
import {
    type CloudRecordingStopper,
    requestCloudRecording,
    useCloudRecording,
    useCloudRecordingStopper,
    useLocalEmployeeId,
    useSecondsSince,
} from '../cloud-recording';
import { type LocalRecording, localRecordingStartedAt, useLocalRecorders, useLocalRecording } from '../local-recording';
import { useMeetingFeatures } from '../meeting-features';
import { RecordingDot } from '../recording-indicator';
import { participantName } from './participant-name';
import classes from './record-button.module.css';
import { i18n } from '@/i18n';
import { notifyError } from '@/shared/notify';
import { UserAvatar } from '@/shared/user-avatar';

const CLOUD_FAILED_NOTIFICATION = 'cloud-recording-failed';

export function RecordButton() {
    const { t } = useTranslation('conference');
    const room = useRoomContext();
    const features = useMeetingFeatures();
    const isEmployee = useLocalEmployeeId() !== null;
    const cloud = useCloudRecording();
    const stopper = useCloudRecordingStopper(cloud.info);
    const local = useLocalRecording();
    const cloudSeconds = cloud.seconds;
    const [opened, setOpened] = useState(false);
    const [confirmStop, setConfirmStop] = useState(false);
    const [transcribe, setTranscribe] = useState(true);
    const [alsoLocal, setAlsoLocal] = useState(false);
    const [cloudFailed, setCloudFailed] = useState(false);
    const [starting, setStarting] = useState(false);

    if (starting && cloud.isRecording) setStarting(false);

    const cloudEnabled = import.meta.env.RECORDING_ENABLED === 'true';
    const localEnabled = features.local_recording;
    const cloudHasContent = cloudEnabled && (isEmployee || cloud.isRecording || cloud.isSaving);
    if (!cloudHasContent && !localEnabled) return null;

    const localActive = local.status === 'recording' || local.status === 'saving';
    const active = cloud.isRecording || localActive;

    const runStart = async (withTranscript: boolean) => {
        setStarting(true);
        try {
            await requestCloudRecording(room.name, 'start', withTranscript);
        } catch (error) {
            setStarting(false);
            console.error(error);
            setCloudFailed(true);
            notifyCloudFailed(() => void runStart(withTranscript));
        }
    };

    const runStop = () => {
        cloud.stop(room.name).catch((error: unknown) => {
            notifyError(t('recording.stopError'), error);
        });
    };

    const startCloud = () => {
        if (room.isE2EEEnabled) {
            notifyError(t('recording.e2eeUnsupported'));
            return;
        }
        if (alsoLocal && localEnabled && local.status === 'idle') void local.start();
        setAlsoLocal(false);
        setCloudFailed(false);
        notifications.hide(CLOUD_FAILED_NOTIFICATION);
        setOpened(false);
        void runStart(features.transcription && transcribe);
    };

    let label: ReactNode = t('recording.button');
    if (cloud.isRecording) label = formatDuration(cloudSeconds);
    else if (localActive) label = formatDuration(local.elapsedSeconds);

    return (
        <>
            <Popover opened={opened} position="top" shadow="md" width="var(--spacing-96)" onChange={setOpened}>
                <Popover.Target>
                    <Button
                        aria-expanded={opened}
                        aria-label={t('recording.menu.trigger')}
                        color={active ? 'danger' : undefined}
                        data-testid="record-button"
                        leftSection={active ? <RecordingDot tone="light" /> : <IconPlayerRecord size={20} />}
                        radius="var(--radius-rounded-full)"
                        rightSection={<IconChevronUp size={16} />}
                        size="lg"
                        variant={active ? 'light' : 'default'}
                        onClick={() => {
                            setOpened((value) => !value);
                        }}
                    >
                        <span className={classes.time}>{label}</span>
                    </Button>
                </Popover.Target>
                <Popover.Dropdown data-testid="record-popover" p="var(--spacing-2)">
                    <Stack gap="var(--spacing-2)">
                        {cloudEnabled && (
                            <CloudSection
                                alsoLocal={alsoLocal}
                                canStart={isEmployee}
                                cloudFailed={cloudFailed}
                                cloudSeconds={cloudSeconds}
                                local={localEnabled ? local : undefined}
                                starting={starting}
                                stopper={stopper}
                                transcribe={features.transcription ? transcribe : undefined}
                                onAlsoLocalChange={setAlsoLocal}
                                onStart={startCloud}
                                onTranscribeChange={setTranscribe}
                                onStop={() => {
                                    setOpened(false);
                                    setConfirmStop(true);
                                }}
                            />
                        )}
                        {localEnabled && <LocalSection local={local} />}
                    </Stack>
                </Popover.Dropdown>
            </Popover>
            <Modal
                centered
                opened={confirmStop}
                title={t('recording.confirmStop.title')}
                onClose={() => {
                    setConfirmStop(false);
                }}
            >
                <Text size="sm">{t('recording.confirmStop.message')}</Text>
                <Group gap="var(--spacing-2)" justify="flex-end" mt="var(--spacing-5)">
                    <Button
                        variant="default"
                        onClick={() => {
                            setConfirmStop(false);
                        }}
                    >
                        {t('recording.cancel')}
                    </Button>
                    <Button
                        color="danger"
                        data-testid="confirm-stop-cloud"
                        onClick={() => {
                            setConfirmStop(false);
                            runStop();
                        }}
                    >
                        {t('recording.confirmStop.confirm')}
                    </Button>
                </Group>
            </Modal>
        </>
    );
}

function notifyCloudFailed(retry: () => void) {
    notifications.show({
        id: CLOUD_FAILED_NOTIFICATION,
        color: 'danger',
        autoClose: false,
        title: i18n.t('conference:recording.notify.cloudFailed.title'),
        message: (
            <Stack align="flex-start" gap="var(--spacing-2)">
                <Text size="sm">{i18n.t('conference:recording.notify.cloudFailed.message')}</Text>
                <Button
                    size="sm"
                    variant="default"
                    onClick={() => {
                        notifications.hide(CLOUD_FAILED_NOTIFICATION);
                        retry();
                    }}
                >
                    {i18n.t('conference:recording.notify.cloudFailed.retry')}
                </Button>
            </Stack>
        ),
    });
}

function SectionHeader({ icon, title, scope }: Readonly<{ icon: ReactNode; title: string; scope: string }>) {
    return (
        <Group justify="space-between" wrap="nowrap">
            <Group gap="var(--spacing-2)" wrap="nowrap">
                {icon}
                <Text fw={600} size="sm">
                    {title}
                </Text>
            </Group>
            <Badge color="gray" variant="light">
                {scope}
            </Badge>
        </Group>
    );
}

type CloudSectionProps = {
    canStart: boolean;
    stopper: CloudRecordingStopper | null;
    cloudSeconds: number;
    starting: boolean;
    cloudFailed: boolean;
    transcribe: boolean | undefined;
    alsoLocal: boolean;
    local: LocalRecording | undefined;
    onTranscribeChange: (value: boolean) => void;
    onAlsoLocalChange: (value: boolean) => void;
    onStart: () => void;
    onStop: () => void;
};

function CloudSection({
    canStart,
    stopper,
    cloudSeconds,
    starting,
    cloudFailed,
    transcribe,
    alsoLocal,
    local,
    onTranscribeChange,
    onAlsoLocalChange,
    onStart,
    onStop,
}: Readonly<CloudSectionProps>) {
    const { t } = useTranslation('conference');
    const { isRecording, isSaving, info } = useCloudRecording();
    const time = formatDuration(cloudSeconds);

    return (
        <div className={classes.section} data-testid="cloud-recording-section">
            <SectionHeader icon={<IconCloud size={20} />} scope={t('recording.cloud.scope')} title={t('recording.cloud.title')} />
            {isRecording && (
                <>
                    <Group gap="var(--spacing-2)">
                        <RecordingDot tone="light" />
                        <Text className={classes.time} size="sm">
                            {info?.startedBy && (!stopper?.isStarter || !stopper.isLocal)
                                ? t('recording.cloud.running', { time, name: info.startedBy.name })
                                : t('recording.cloud.runningOwn', { time })}
                        </Text>
                        {info?.transcribe && (
                            <Badge color="blue" variant="light">
                                {t('recording.cloud.withTranscript')}
                            </Badge>
                        )}
                    </Group>
                    {stopper?.isLocal && (
                        <Button
                            fullWidth
                            color="danger"
                            data-testid="stop-cloud-recording"
                            leftSection={<IconPlayerStop size={20} />}
                            variant="default"
                            onClick={onStop}
                        >
                            {t('recording.cloud.stop')}
                        </Button>
                    )}
                    {stopper && !stopper.isLocal && (
                        <Text c="dimmed" data-testid="cloud-recording-stopper" size="xs">
                            {t('recording.cloud.stopBy', { name: stopper.name })}
                        </Text>
                    )}
                </>
            )}
            {isSaving && (
                <Text className={classes.time} data-testid="cloud-recording-saving-status" size="sm">
                    {t('recording.cloud.saving', { time })}
                </Text>
            )}
            {!isRecording && canStart && (
                <>
                    <Text c="dimmed" size="xs">
                        {t('recording.cloud.hint')}
                    </Text>
                    {transcribe !== undefined && (
                        <Switch
                            checked={transcribe}
                            data-testid="transcribe-switch"
                            label={t('recording.cloud.transcribe')}
                            onChange={(event) => {
                                onTranscribeChange(event.currentTarget.checked);
                            }}
                        />
                    )}
                    {local && (
                        <Checkbox
                            checked={alsoLocal && local.support.supported}
                            data-testid="also-local-checkbox"
                            disabled={!local.support.supported || local.status !== 'idle'}
                            label={t('recording.cloud.alsoLocal')}
                            description={
                                local.support.supported ? t('recording.cloud.alsoLocalHint') : t('recording.local.unsupported')
                            }
                            onChange={(event) => {
                                onAlsoLocalChange(event.currentTarget.checked);
                            }}
                        />
                    )}
                    {cloudFailed && (
                        <Group gap="var(--spacing-2)" wrap="nowrap">
                            <IconAlertTriangle color="var(--color-danger-default)" size={16} />
                            <Text c="danger" size="xs">
                                {t('recording.cloud.startFailed')}
                            </Text>
                        </Group>
                    )}
                    <Button
                        fullWidth
                        data-testid="start-cloud-recording"
                        disabled={isSaving}
                        leftSection={<IconPlayerRecord size={20} />}
                        loading={starting}
                        variant={isSaving ? 'default' : 'filled'}
                        onClick={onStart}
                    >
                        {cloudFailed ? t('recording.cloud.retry') : t('recording.cloud.start')}
                    </Button>
                    {isSaving && (
                        <Text c="dimmed" size="xs">
                            {t('recording.cloud.savingPrevious')}
                        </Text>
                    )}
                </>
            )}
            {!isRecording && !isSaving && !canStart && (
                <Text c="dimmed" size="xs">
                    {t('recording.cloud.byEmployee')}
                </Text>
            )}
        </div>
    );
}

function LocalSection({ local }: Readonly<{ local: LocalRecording }>) {
    const { t } = useTranslation('conference');
    const others = useLocalRecorders().filter((participant) => !participant.isLocal);
    const { support, status } = local;

    let body: ReactNode;
    if (!support.supported) {
        body = (
            <Group align="flex-start" data-testid="local-recording-unsupported" gap="var(--spacing-2)" wrap="nowrap">
                <IconAlertTriangle color="var(--color-warning-default)" size={16} />
                <Stack gap="var(--spacing-0-5)">
                    <Text size="sm">{t('recording.local.unsupported')}</Text>
                    <Text c="dimmed" size="xs">
                        {support.reason === 'browser'
                            ? t('recording.local.unsupportedHint')
                            : t('recording.local.unsupportedCapabilities')}
                    </Text>
                </Stack>
            </Group>
        );
    } else if (status === 'recording' || status === 'saving') {
        body = (
            <>
                <Group gap="var(--spacing-2)">
                    <RecordingDot tone="light" />
                    <Text className={classes.time} size="sm">
                        {t('recording.local.mine', { time: formatDuration(local.elapsedSeconds) })}
                    </Text>
                </Group>
                <Button
                    fullWidth
                    color="danger"
                    data-testid="stop-local-recording"
                    disabled={status === 'saving'}
                    leftSection={status === 'saving' ? <Loader size="xs" /> : <IconPlayerStop size={20} />}
                    variant="default"
                    onClick={() => void local.stop()}
                >
                    {status === 'saving' ? t('recording.local.saving') : t('recording.local.stop')}
                </Button>
            </>
        );
    } else {
        body = (
            <>
                <Text c="dimmed" size="xs">
                    {t('recording.local.hint')}
                </Text>
                <Button
                    fullWidth
                    data-testid="start-local-recording"
                    disabled={status === 'starting'}
                    leftSection={status === 'starting' ? <Loader size="xs" /> : <IconDeviceDesktopDown size={20} />}
                    variant="light"
                    onClick={() => void local.start()}
                >
                    {status === 'starting' ? t('recording.local.starting') : t('recording.local.start')}
                </Button>
            </>
        );
    }

    return (
        <div className={classes.section} data-testid="local-recording-section">
            <SectionHeader
                icon={<IconDeviceDesktopDown size={20} />}
                scope={t('recording.local.scope')}
                title={t('recording.local.title')}
            />
            {body}
            {others.length > 0 && (
                <>
                    <Divider />
                    <Text c="dimmed" size="xs">
                        {t('recording.local.others')}
                    </Text>
                    {others.map((participant) => (
                        <RecorderRow key={participant.identity} participant={participant} />
                    ))}
                </>
            )}
        </div>
    );
}

function RecorderRow({ participant }: Readonly<{ participant: Participant }>) {
    const name = participantName(participant.name, participant.identity);
    const seconds = useSecondsSince(localRecordingStartedAt(participant.attributes) || undefined);
    return (
        <Group gap="var(--spacing-2)" wrap="nowrap">
            <UserAvatar name={name} radius="var(--radius-rounded-full)" size="sm" />
            <Stack gap={0} miw={0}>
                <Text truncate size="sm">
                    {name}
                </Text>
                <Text c="dimmed" className={classes.time} size="xs">
                    {formatDuration(seconds)}
                </Text>
            </Stack>
        </Group>
    );
}
